/**
 * The editor's extension list: §5.1's closed component set, plus the affordances
 * that exist only in a browser.
 *
 * The first line of `editorExtensions` is the important one. `documentExtensions`
 * comes from `@matchbox/shared/markdown/schema` — the single definition of the
 * component set, shared with the serializer and the collab server so that "a
 * component that exists in one and not the others cannot happen" is structural
 * rather than a convention someone remembers. Everything added on top of it here
 * adds behaviour and **no node and no mark**; `roundtrip.test.ts` asserts that,
 * because a node type the editor can create and the serializer cannot write
 * serializes to nothing and takes the paragraph with it.
 *
 * Three omissions are decisions, not oversights:
 *
 * - **`StarterKit` is not used**, though it is installed. It redefines
 *   `document`, `paragraph`, `text`, `heading`, the lists and more, which throws
 *   on duplicate extension names, and the extensions it adds beyond the closed
 *   set have no markdown spelling — storing them would make the derived
 *   `markdown` column a lie (ADR 0002).
 * - **`UndoRedo` is not installed.** `Collaboration` brings Yjs's `UndoManager`,
 *   and a second history stack alongside it undoes a co-author's typing as
 *   readily as your own. This is the single easiest way to corrupt a shared
 *   document, and the guard is simply never adding the extension.
 * - **`TrailingNode` is not installed.** It appends an empty paragraph after the
 *   last block, which is a real editing convenience and also a document
 *   modification: the paragraph reaches the derived markdown as a `{#}` line and
 *   fills §7.5's diffs with a change nobody typed. `Gapcursor` solves the
 *   navigation half of the problem without touching the document.
 */

import type { HocuspocusProvider } from '@hocuspocus/provider';
import { documentExtensions } from '@matchbox/shared/markdown';
import type { Extensions } from '@tiptap/core';
import { Collaboration } from '@tiptap/extension-collaboration';
import { CollaborationCaret } from '@tiptap/extension-collaboration-caret';
import { Dropcursor, Gapcursor, Placeholder } from '@tiptap/extensions';
import type * as Y from 'yjs';
import { ExternalLinkMarker } from './external-links.ts';
import type { PresenceFace } from './identity.ts';

export const DEFAULT_PLACEHOLDER = 'Start writing…';

export interface EditorExtensionOptions {
  placeholder?: string;
}

/**
 * Everything that shapes the document, and nothing that needs a socket.
 *
 * Callable without a provider on purpose: a reader resolving to the published
 * version (§7.4) renders with exactly this list and never opens a Yjs
 * connection, so the read-only surface and the editing surface cannot drift into
 * two different schemas.
 */
export function editorExtensions(options: EditorExtensionOptions = {}): Extensions {
  return [
    // `.configure()` with no arguments, which returns a copy rather than the
    // shared instance. `documentExtensions` is a module-level array that the API
    // and the collab server also build their schema from, and Tiptap hands an
    // extension's `storage` object straight to the editor
    // (`extensionStorage[name] = extension.storage`) — so two editors built from
    // the shared instances share one mutable storage object. `UniqueID` keeps
    // `needsInitialIdGeneration` there, and two editors racing on that flag is a
    // document whose blocks never get ids. Given what §5.1 hangs off block ids,
    // a copy per editor is the cheapest possible insurance. The copies are
    // schema-identical, which `roundtrip.test.ts` asserts.
    ...documentExtensions.map((extension) => extension.configure()),
    Dropcursor.configure({ width: 2, class: 'mx-dropcursor' }),
    Gapcursor.configure(),
    Placeholder.configure({ placeholder: options.placeholder ?? DEFAULT_PLACEHOLDER }),
    ExternalLinkMarker.configure(),
  ];
}

export interface CollaborationExtensionOptions {
  /** The `Y.Doc` the provider is syncing. */
  document: Y.Doc;
  provider: HocuspocusProvider;
  /** Published on the awareness channel and painted on this person's caret. */
  user: PresenceFace;
}

/**
 * The two extensions that turn the editor into a participant in a shared
 * document (§8.3).
 *
 * Separate from `editorExtensions` because they cannot exist without a live
 * connection, and because keeping them apart is what lets the round-trip test
 * build the editor's real schema without standing up a WebSocket.
 *
 * The Yjs fragment name is left at its default (`default`). It is a protocol
 * between this editor and every other client on the same `Y.Doc`, in the same
 * way the document name is: two clients reading different fragments of one
 * document see two empty documents and overwrite each other silently.
 */
export function collaborationExtensions(options: CollaborationExtensionOptions): Extensions {
  return [
    Collaboration.configure({ document: options.document }),
    CollaborationCaret.configure({
      provider: options.provider,
      user: { personId: options.user.personId, name: options.user.name, color: options.user.color },
    }),
  ];
}
